import type { ConditionalFormRule, FormRulesStructure } from '../model/form-rules.types';
import type { FormElement } from '../model/form.types';
import { getFieldDefinition } from './registry';
import type { FieldValue } from '../model/types';

export interface RuleFieldSnapshot {
  element: FormElement;
  value: FieldValue;
}

export interface RuleOutcome {
  visible: boolean;
  enabled: boolean;
}

interface RuleEffect {
  action: 'show' | 'hide' | 'disable';
  targetFieldIds: string[];
}

function effectsOf(rule: ConditionalFormRule): RuleEffect[] {
  const blocks = rule.thenBlocks.filter((block) => block.action !== '');
  if (blocks.length > 0) {
    return blocks.map((block) => ({
      action: block.action as RuleEffect['action'],
      targetFieldIds: block.targets.flatMap((target) => target.fieldIds),
    }));
  }
  return [{ action: rule.action, targetFieldIds: rule.targetFieldIds }];
}

interface RuleTargetSets {
  showTargets: Set<string>;
  shown: Set<string>;
  hidden: Set<string>;
  disabled: Set<string>;
}

function applyEffect(effect: RuleEffect, fires: boolean, sets: RuleTargetSets): void {
  for (const fieldId of effect.targetFieldIds) {
    if (effect.action === 'show') {
      sets.showTargets.add(fieldId);
      if (fires) sets.shown.add(fieldId);
    } else if (fires && effect.action === 'hide') {
      sets.hidden.add(fieldId);
    } else if (fires && effect.action === 'disable') {
      sets.disabled.add(fieldId);
    }
  }
}

function ruleFires(rule: ConditionalFormRule, fields: Record<string, RuleFieldSnapshot>): boolean {
  const trigger = fields[rule.triggerFieldId];
  if (!trigger) return false;
  const definition = getFieldDefinition(trigger.element);
  if (!definition) return false;
  return definition.triggerMatches(trigger.element, trigger.value, rule.triggerOption);
}

function emptySets(): RuleTargetSets {
  return { showTargets: new Set(), shown: new Set(), hidden: new Set(), disabled: new Set() };
}

// A field targeted by any "show" rule starts hidden and appears only while a show rule
// fires. "hide" always wins over "show" when both fire on the same field.
export function evaluateRules(
  rules: FormRulesStructure | null,
  fields: Record<string, RuleFieldSnapshot>,
): (fieldId: string) => RuleOutcome {
  const sets = emptySets();

  for (const rule of rules?.rules ?? []) {
    const fires = ruleFires(rule, fields);
    for (const effect of effectsOf(rule)) applyEffect(effect, fires, sets);
  }

  const { showTargets, shown, hidden, disabled } = sets;

  return (fieldId) => ({
    visible: !hidden.has(fieldId) && (!showTargets.has(fieldId) || shown.has(fieldId)),
    enabled: !disabled.has(fieldId),
  });
}

// A rule reads its trigger and changes its targets. When that graph has a cycle, whether a field is
// shown can depend on itself and the fill has no single answer to keep, so the fill refuses such a
// matrix. Each group is one strongly connected set of field ids, in rule order. A rule whose trigger
// or target is not in knownIds never fires on it, so it closes no cycle.
export function ruleCycleIds(rules: FormRulesStructure | null, knownIds?: ReadonlySet<string>): string[][] {
  const known = (id: string): boolean => knownIds === undefined || knownIds.has(id);
  const edges = new Map<string, string[]>();
  for (const rule of rules?.rules ?? []) {
    if (!known(rule.triggerFieldId)) continue;
    for (const effect of effectsOf(rule)) {
      const targets = effect.targetFieldIds.filter(known);
      edges.set(rule.triggerFieldId, [...(edges.get(rule.triggerFieldId) ?? []), ...targets]);
    }
  }

  const index = new Map<string, number>();
  const low = new Map<string, number>();
  const stack: string[] = [];
  const onStack = new Set<string>();
  const cycles: string[][] = [];

  const visit = (id: string): void => {
    index.set(id, index.size);
    low.set(id, index.get(id) ?? 0);
    stack.push(id);
    onStack.add(id);
    for (const next of edges.get(id) ?? []) {
      if (!index.has(next)) {
        visit(next);
        low.set(id, Math.min(low.get(id) ?? 0, low.get(next) ?? 0));
      } else if (onStack.has(next)) {
        low.set(id, Math.min(low.get(id) ?? 0, index.get(next) ?? 0));
      }
    }
    if (low.get(id) !== index.get(id)) return;
    const component: string[] = [];
    let member: string | undefined;
    do {
      member = stack.pop();
      if (member === undefined) break;
      onStack.delete(member);
      component.push(member);
    } while (member !== id);
    if (component.length > 1 || (edges.get(id) ?? []).includes(id)) cycles.push(component.reverse());
  };

  for (const id of edges.keys()) {
    if (!index.has(id)) visit(id);
  }
  return cycles;
}
